import {
  Button, Card, DataTable, ErrorState, LoadingState, Notice, PageHeader, Row, Stack, useApi, useI18n, useRoleLabel,
} from '@daghfal/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

/**
 * Password resets waiting for the nursery to confirm the person face to face (Q25).
 *
 * They come from people whose accounts lay unused too long for a texted code alone, and who belong to this
 * nursery only. Approving lets the person set a new password with a fresh code; refusing changes nothing.
 */
export function AccountRequests() {
  const api = useApi();
  const { t, formatPhone, formatDateTime } = useI18n();
  const roleLabel = useRoleLabel();
  const queryClient = useQueryClient();

  const requests = useQuery({ queryKey: ['account-requests'], queryFn: () => api.accountRecovery.list() });

  const decide = useMutation({
    mutationFn: ({ id, approve }: { id: number; approve: boolean }) =>
      approve ? api.accountRecovery.approve(id) : api.accountRecovery.reject(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['account-requests'] }),
  });

  return (
    <Stack>
      <PageHeader title={t('accountRequests.title')} lede={t('accountRequests.lede')} />

      {decide.isError && <ErrorState error={decide.error} />}
      {decide.isSuccess && decide.variables.approve && <Notice tone="success">{t('accountRequests.approved')}</Notice>}
      {requests.isPending && <LoadingState />}
      {requests.isError && <ErrorState error={requests.error} onRetry={() => void requests.refetch()} />}
      {requests.data?.items.length === 0 && <p className="dg-card__hint">{t('accountRequests.empty')}</p>}

      {requests.data && requests.data.items.length > 0 && (
        <Card flush>
          <DataTable
            caption={t('accountRequests.title')}
            rows={requests.data.items}
            rowKey={(request) => request.id}
            columns={[
              { key: 'name', header: t('staff.name'), render: (r) => r.fullName ?? '—' },
              { key: 'phone', header: t('staff.phone'), render: (r) => formatPhone(r.phone) },
              { key: 'role', header: t('staff.role'), render: (r) => roleLabel(r.roleKey) ?? '—' },
              { key: 'requested', header: t('accountRequests.requestedAt'), render: (r) => formatDateTime(r.requestedAt) },
              {
                key: 'from',
                header: t('accountRequests.from'),
                render: (r) => <span dir="ltr">{[r.requestedFromIp, r.userAgent].filter(Boolean).join(' · ') || '—'}</span>,
              },
              {
                key: 'actions',
                header: '',
                render: (r) => (
                  <Row>
                    <Button
                      variant="secondary"
                      busy={decide.isPending && decide.variables.id === r.id && decide.variables.approve}
                      onClick={() => decide.mutate({ id: r.id, approve: true })}
                    >
                      {t('accountRequests.approve')}
                    </Button>
                    <Button
                      variant="ghost"
                      busy={decide.isPending && decide.variables.id === r.id && !decide.variables.approve}
                      onClick={() => decide.mutate({ id: r.id, approve: false })}
                    >
                      {t('accountRequests.reject')}
                    </Button>
                  </Row>
                ),
              },
            ]}
          />
        </Card>
      )}
    </Stack>
  );
}
